import React, { useState, useEffect } from 'react';
import { UserPermissions } from '../../types';

export interface HeaderActionMenuProps {
  userPermissions?: UserPermissions;
  onOpenDashboard?: () => void;
  onOpenHR?: () => void;
  onOpenAbsences?: () => void;
  onDownloadEmployeesReport?: () => void;
  onDownloadAbsencesReport?: () => void;
}

/**
 * Menu principal do header (Dashboard, RH, Faltas, Relatórios).
 * Extraído de Header.tsx para manter o componente pai enxuto.
 */
export const HeaderActionMenu: React.FC<HeaderActionMenuProps> = ({
  userPermissions,
  onOpenDashboard,
  onOpenHR,
  onOpenAbsences,
  onDownloadEmployeesReport,
  onDownloadAbsencesReport,
}) => {
  const [isMainMenuOpen, setIsMainMenuOpen] = useState(false);
  const [isReportsMenuOpen, setIsReportsMenuOpen] = useState(false);

  useEffect(() => {
    if (!isMainMenuOpen) setIsReportsMenuOpen(false);
  }, [isMainMenuOpen]);

  const showMenu = userPermissions?.dashboard || userPermissions?.hr || userPermissions?.absences;
  if (!showMenu) return null;

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsMainMenuOpen(!isMainMenuOpen)}
        className={`flex h-7 items-center justify-center rounded-full px-3 text-[11px] font-semibold transition-all group border ${isMainMenuOpen ? 'bg-primary-accent text-white border-primary-accent shadow-lg shadow-primary-accent/20' : 'bg-slate-100 text-slate-600 border-transparent hover:bg-slate-200'}`}
        aria-expanded={isMainMenuOpen}
      >
        <span className={`material-symbols-outlined mr-1.5 !text-[14px] transition-colors ${isMainMenuOpen ? 'text-white' : 'text-slate-400 group-hover:text-primary-accent'}`}>apps</span>
        Menu
        <span className={`material-symbols-outlined ml-1 !text-[12px] transition-transform ${isMainMenuOpen ? 'rotate-180' : ''}`}>expand_more</span>
      </button>

      {isMainMenuOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsMainMenuOpen(false)} aria-hidden="true" />
          <div className="absolute left-0 top-full mt-1.5 w-52 bg-white rounded-xl shadow-xl border border-slate-100 p-1.5 z-50 animate-in fade-in zoom-in-95 slide-in-from-top-2 duration-200 origin-top-left">
            {userPermissions?.dashboard && (
              <button
                type="button"
                onClick={() => { setIsMainMenuOpen(false); onOpenDashboard?.(); }}
                className="flex items-center w-full gap-2 px-3 py-1.5 rounded-lg text-[11px] font-semibold text-slate-600 hover:bg-slate-50 transition-colors group"
              >
                <span className="material-symbols-outlined !text-[14px] text-slate-400 group-hover:text-primary-accent transition-colors">dashboard</span>
                Dashboard
              </button>
            )}
            {userPermissions?.hr && (
              <button
                type="button"
                onClick={() => { setIsMainMenuOpen(false); onOpenHR?.(); }}
                className="flex items-center w-full gap-2 px-3 py-1.5 rounded-lg text-[11px] font-semibold text-slate-600 hover:bg-slate-50 transition-colors group"
              >
                <span className="material-symbols-outlined !text-[14px] text-slate-400 group-hover:text-secondary-accent transition-colors">work</span>
                RH
              </button>
            )}
            {userPermissions?.absences && (
              <button
                type="button"
                onClick={() => { setIsMainMenuOpen(false); onOpenAbsences?.(); }}
                className="flex items-center w-full gap-2 px-3 py-1.5 rounded-lg text-[11px] font-semibold text-slate-600 hover:bg-slate-50 transition-colors group"
              >
                <span className="material-symbols-outlined !text-[14px] text-slate-400 group-hover:text-pink-500 transition-colors">event_busy</span>
                Faltas
              </button>
            )}
            <div className="relative">
              <button
                type="button"
                onClick={() => setIsReportsMenuOpen(!isReportsMenuOpen)}
                className="flex items-center justify-between w-full gap-2 px-3 py-1.5 rounded-lg text-[11px] font-semibold text-slate-600 hover:bg-slate-50 transition-colors group"
              >
                <span className="flex items-center gap-2">
                  <span className="material-symbols-outlined !text-[14px] text-slate-400 group-hover:text-indigo-500 transition-colors">description</span>
                  Relatórios
                </span>
                <span className={`material-symbols-outlined !text-[12px] text-slate-400 transition-transform ${isReportsMenuOpen ? 'rotate-180' : ''}`}>expand_more</span>
              </button>
              {isReportsMenuOpen && (
                <div className="mt-1 ml-2 rounded-lg border border-slate-100 bg-slate-50/70 p-1">
                  <button
                    type="button"
                    onClick={() => {
                      setIsReportsMenuOpen(false);
                      setIsMainMenuOpen(false);
                      onDownloadEmployeesReport?.();
                    }}
                    className="flex items-center w-full gap-2 px-2 py-1.5 rounded-md text-[10px] font-semibold text-slate-600 hover:bg-white transition-colors"
                  >
                    <span className="material-symbols-outlined !text-[12px] text-indigo-500">group</span>
                    Relatório de Colaboradores
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setIsReportsMenuOpen(false);
                      setIsMainMenuOpen(false);
                      onDownloadAbsencesReport?.();
                    }}
                    className="flex items-center w-full gap-2 px-2 py-1.5 rounded-md text-[10px] font-semibold text-slate-600 hover:bg-white transition-colors"
                  >
                    <span className="material-symbols-outlined !text-[12px] text-pink-500">event_busy</span>
                    Relatório de Faltas
                  </button>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
